import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  Divider,
  Autocomplete,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Snackbar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaymentsIcon from "@mui/icons-material/Payments";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ContactEmergencyIcon from "@mui/icons-material/ContactEmergency";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUsers } from "../hooks/useUsers";
import { useResidents } from "../hooks/useResidents";
import masterDataService from '../services/masterDataService';
import appointmentService from '../services/appointmentService';
import vitalsService from '../services/vitalsService';
import residentService from '../services/residentService';
import { ResidentDTO, UploadedDocument } from '../types/resident.types';
import { formatToBackendDateTime, formatToLocalDateTime } from '../utils/dateUtils';
import { isValidNpi } from '../utils/npiUtils';
import { isValidTaxId, formatTaxId } from '../utils/taxIdUtils';
import {
  isValidPosCode,
  validateCptHcpcsCodes,
  formatCptHcpcsInput,
  validateChargeAmount,
  validateAuthorizationNumber,
  validateCopayAmount,
  validateCoinsurancePercentage,
  validateAnnualDeductible,
  validateRemainingDeductible,
  validateOutOfPocketLimit,
} from '../utils/billingCodesUtils';

// Validation schema
const admissionSchema = yup.object({
  // Personal Information - Tab 1
  firstName: yup.string().required("First Name is required").max(100, "First Name must be at most 100 characters").matches(/^[A-Za-z\s]*$/, "Only alphabets are allowed"),
  middleName: yup.string().optional().nullable().max(100, "Middle Name must be at most 100 characters").matches(/^[A-Za-z\s]*$/, "Only alphabets are allowed"),
  lastName: yup.string().required("Last Name is required").max(100, "Last Name must be at most 100 characters").matches(/^[A-Za-z\s]*$/, "Only alphabets are allowed"),
  dateOfBirth: yup
    .date()
    .required("Date of birth is required")
    .typeError("Date of birth is required")
    .min(new Date("1900-01-01"), "Date of birth cannot be before the year 1900")
    .test("not-future", "Date of birth must be in the past", (value) => {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return value < today;
    }),
  gender: yup.string().required("Gender is required"),
  address: yup.string().required("Address is required").max(500, "Address must be at most 500 characters"),
  city: yup.string().nullable().optional().max(100, "City must be at most 100 characters"),
  state: yup.string().nullable().optional().max(100, "State must be at most 100 characters"),
  zipCode: yup.string().nullable().optional().matches(/^[a-zA-Z0-9]*$/, "Zip Code must contain only numbers and alphabets"),
  postalCode: yup.string().nullable().optional(),
  roomNumber: yup.string().required("Room Number is required").matches(/^[a-zA-Z0-9]*$/, "Room Number must contain only numbers and alphabets"),
  bedNumber: yup.string().required("Bed Number is required").matches(/^[a-zA-Z0-9]*$/, "Bed Number must contain only numbers and alphabets"),
  ssn: yup.string().optional().max(11, "SSN must be at most 11 characters"),
  admissionType: yup.string().required("Admission type is required"),
  primaryCarePhysician: yup
    .string()
    .required("Physician Name is required")
    .matches(/^[A-Za-z\s]*$/, "Only alphabets are allowed"),
  attendingPhysicianPhone: yup
    .string()
    .required("Contact Number is required")
    .matches(/^\d{10}$/, "Must be exactly 10 digits"),
  attendingPhysicianEmail: yup
    .string()
    .trim()
    .required("Email Address is required")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email address"
    ),
  pcpAddress: yup.string().nullable().optional(),
  pcpNPI: yup
    .string()
    .nullable()
    .optional()
    .matches(/^\d*$/, "Only numbers are allowed"),
  preferredPharmacy: yup.string().nullable().optional(),

  // Clinical Details - Tab 2
  admissionDate: yup
    .date()
    .required("Admission date is required")
    .typeError("Admission date is required")
    .max(new Date(), "Admission cannot be in future"),
  attendingPhysician: yup.string().required("Attending Physician is required").matches(/^[A-Za-z\s]*$/, "Only alphabets are allowed"),
  primaryDiagnosis: yup.string().nullable().optional().max(700, "Primary Diagnosis must be at most 700 characters"),
  bloodPressure: yup.string().required("Blood Pressure is required").nullable(),
  height: yup.string().required("Height is required").nullable(),
  weight: yup.string().required("Weight is required").nullable(),
  bloodGroup: yup.string().nullable().optional(),
  allergies: yup.string().nullable().optional(),


  medicalRecordNumber: yup
    .string()
    .required("Unique Medical Record Number (MRN) is required")

    .nullable(),
  mentalCondition: yup.string().nullable().optional(),
  physicalDisabilities: yup.string().nullable().optional(),
  dnrStatus: yup.string().required("DNR Status is required"),
  hospitalName: yup.string().nullable().optional(),
  doctorName: yup.string().nullable().optional(),
  hospitalAddress: yup.string().nullable().optional(),

  // Emergency Contact - separate from Insurance
  emergencyContactName: yup.string().trim().required("Emergency Contact Name is required").max(200, "Name must be at most 200 characters"),
  emergencyContactRelationship: yup.string().nullable().optional(),
  emergencyContactPhone: yup
    .string()
    .required("Emergency Contact Phone is required")
    .matches(/^\d{10}$/, "Must be exactly 10 digits"),
  emergencyContactEmail: yup
    .string()
    .nullable()
    .optional()
    .test("valid-email", "Invalid email address", (value) => {
      if (!value || value.trim() === "") return true;
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim());
    }),
  emergencyContactAddress: yup.string().nullable().optional().max(500, "Address must be at most 500 characters"),

  // Insurance Information - Tab 3
  insuranceCompanyName: yup.string().nullable().optional(),
  insuranceType: yup.string().nullable(),
  payerId: yup
    .string()
    .nullable()
    .optional()
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .test(
      "valid-payer-id",
      "Payer ID must contain only letters and numbers (no spaces or special characters, max 35 characters)",
      (value) => {
        if (!value || value.trim() === "") return true;
        return /^[A-Za-z0-9]{1,35}$/.test(value.trim());
      }
    ),
  insurancePhone: yup
    .string()
    .nullable()
    .matches(/^\d{10}$/, {
      message: "Must be exactly 10 digits",
      excludeEmptyString: true,
    }),
  insuranceEmail: yup
    .string()
    .nullable()
    .optional()
    .test("valid-email", "Invalid email address", (value) => {
      if (!value || value.trim() === "") return true;
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim());
    }),
  subscriberName: yup.string().nullable().optional(),
  subscriberRelationship: yup.string().nullable(),
  subscriberDob: yup.date().nullable().optional(),
  subscriberGender: yup.string().nullable(),
  subscriberPhone: yup.string().nullable().optional(),
  subscriberAddress: yup.string().nullable().optional(),
  subscriberEmployerName: yup.string().nullable().optional(),
  subscriberEmployerAddress: yup.string().nullable().optional(),

  // Policy Details - Tab 3 continued
  policyMemberId: yup.string().nullable(),

  groupNumber: yup.string().optional(),
  planName: yup.string().optional(),
  planType: yup.string().nullable(),

  coverageType: yup.string().nullable(),

  effectiveDate: yup.date().optional().nullable(),
  terminationDate: yup
    .date()
    .optional()
    .nullable()
    .when("effectiveDate", {
      is: "effectiveDate", // Reference the field directly
      then: (schema) =>
        schema
          .min(
            yup.ref("effectiveDate"),
            "Termination date must be after effective date"
          )
          .typeError("Invalid termination date"),
      otherwise: (schema) => schema.nullable(),
    }),
  referralRequired: yup.boolean().optional(),
  priorAuthRequired: yup.boolean().optional(),
  authorizationNumber: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-auth-number",
      function (val) {
        const isPriorAuthRequired = Boolean(this.parent?.priorAuthRequired);
        const trimmed = (val || "").trim();

        if (isPriorAuthRequired && !trimmed) {
          return this.createError({
            message: "Authorization Number is required when Prior Authorization is required.",
          });
        }

        if (trimmed && validateAuthorizationNumber(trimmed) !== "") {
          return this.createError({
            message: "Please enter a valid authorization number.",
          });
        }

        return true;
      }
    ),
  copayAmount: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-copay",
      "Please enter a valid copay amount.",
      (val) => !val || validateCopayAmount(val) === ""
    ),
  coinsurancePercentage: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-coinsurance",
      "Coinsurance percentage must be between 0 and 100.",
      (val) => !val || validateCoinsurancePercentage(val) === ""
    ),
  annualDeductible: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-annual-deductible",
      "Please enter a valid annual deductible amount.",
      (val) => !val || validateAnnualDeductible(val) === ""
    ),
  remainingDeductible: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-remaining-deductible",
      function (val) {
        if (!val || !val.trim()) return true;
        const annual = this.parent.annualDeductible;
        const err = validateRemainingDeductible(val, annual);
        if (err) {
          return this.createError({ message: err });
        }
        return true;
      }
    ),
  outOfPocketLimit: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-out-of-pocket",
      "Please enter a valid out-of-pocket limit.",
      (val) => !val || validateOutOfPocketLimit(val) === ""
    ),

  // Billing
  billingProviderNpi: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-npi",
      "Please enter a valid 10-digit NPI.",
      (value) => {
        if (!value || !value.trim()) return true;
        const trimmed = value.trim();
        return /^\d{10}$/.test(trimmed) && isValidNpi(trimmed);
      }
    ),
  billingProviderTaxId: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-tax-id",
      "Please enter a valid 9-digit Tax ID (EIN).",
      (value) => {
        if (!value || !value.trim()) return true;
        return isValidTaxId(value.trim());
      }
    ),
  billingProviderAddress: yup.string().optional(),
  renderingProviderName: yup.string().optional(),
  renderingProviderNpi: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-npi",
      "Please enter a valid 10-digit NPI.",
      (value) => {
        if (!value || !value.trim()) return true;
        const trimmed = value.trim();
        return /^\d{10}$/.test(trimmed) && isValidNpi(trimmed);
      }
    ),
  facilityNpi: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-npi",
      "Please enter a valid 10-digit NPI.",
      (value) => {
        if (!value || !value.trim()) return true;
        const trimmed = value.trim();
        return /^\d{10}$/.test(trimmed) && isValidNpi(trimmed);
      }
    ),
  placeOfServiceCode: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-pos",
      "Please enter a valid 2-digit POS code.",
      (value) => {
        if (!value || !value.trim()) return true;
        return isValidPosCode(value.trim());
      }
    ),
  icd10DiagnosisCodes: yup.string().nullable().optional(),
  cptHcpcsCodes: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-cpt-hcpcs",
      "Please enter a valid CPT/HCPCS code.",
      (value) => {
        if (!value || !value.trim()) return true;
        return validateCptHcpcsCodes(value.trim()) === "";
      }
    ),
  modifiers: yup.string().optional(),
  chargeAmount: yup
    .string()
    .nullable()
    .optional()
    .test(
      "is-valid-charge-amount",
      function (value) {
        if (!value || !value.trim()) return true;
        const errorMsg = validateChargeAmount(value.trim());
        if (errorMsg) {
          return this.createError({ message: errorMsg });
        }
        return true;
      }
    ),
  claimNotes: yup.string().optional(),
  claimSubmissionMethod: yup
    .string().optional(),

  // Documents
  documentName: yup.string().optional(),
});

type FormData = yup.InferType<typeof admissionSchema>;

const steps = [
  "Personal Information",
  "Clinical Details (Physician Order)",
  "Insurance Information",
  "Upload Documents",
  "Review & Submit",
];

const AdmissionsPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [admissionSuccess, setAdmissionSuccess] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocument[]
  >([]);
  const [currentDocName, setCurrentDocName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [personalInfoCompleted, setPersonalInfoCompleted] = useState(false);

  // Toast state
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { mode = "create", residentId } = location.state || {};
  const isEditMode = mode === "edit";
  const {
    getResidentById,
    updateResident,
    selectedResident,
    loading,
    createResident,
    uploadResidentDocuments,
    residentDocuments,
    fetchResidentDocuments,
    viewResidentDocument,
    deleteResidentDocument,
  } = useResidents();

  const { user } = useAuth();
  const { users, fetchUsers } = useUsers({ autoFetch: isEditMode, initialPageSize: 1000 });

  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [mrnError, setMrnError] = useState<string | null>(null);
  const [mrnChecking, setMrnChecking] = useState(false);
  const [showSSN, setShowSSN] = useState(false);

  const [genderOptions, setGenderOptions] = useState<{ valueName: string }[]>([]);
  const [admissionTypeOptions, setAdmissionTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [insuranceTypeOptions, setInsuranceTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [bloodGroupOptions, setBloodGroupOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [relationshipOptions, setRelationshipOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [planTypeOptions, setPlanTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [coverageTypeOptions, setCoverageTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [claimSubmissionOptions, setClaimSubmissionOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [dnrStatusOptions, setDnrStatusOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);

  useEffect(() => {
    const loadLookupOptions = async () => {
      try {
        const [genderOpts, admissionTypeOpts, bloodGroupOpts, relationshipOpts, planTypeOpts, coverageTypeOpts, claimSubmissionOpts, insuranceTypeOpts, dnrStatusOpts] = await Promise.all([
          masterDataService.getActiveLookupValuesByName('gender'),
          masterDataService.getActiveLookupValuesByName('admissionType'),
          masterDataService.getActiveLookupValuesByName('bloodGroup'),
          masterDataService.getActiveLookupValuesByName('subscriberRelationship'),
          masterDataService.getActiveLookupValuesByName('PlanType'),
          masterDataService.getActiveLookupValuesByName('coverageType'),
          masterDataService.getActiveLookupValuesByName('ClaimSubmissionMethod'),
          masterDataService.getActiveLookupValuesByName('InsuranceType'),
          masterDataService.getActiveLookupValuesByName('DNRStatus'),
        ]);
        setGenderOptions(genderOpts);
        setAdmissionTypeOptions(admissionTypeOpts);
        setBloodGroupOptions(bloodGroupOpts);
        setRelationshipOptions(relationshipOpts);
        setPlanTypeOptions(planTypeOpts);
        setCoverageTypeOptions(coverageTypeOpts);
        setClaimSubmissionOptions(claimSubmissionOpts);
        setInsuranceTypeOptions(insuranceTypeOpts);
        setDnrStatusOptions(dnrStatusOpts);
      } catch (err) {
        console.error('Failed to load lookup options', err);
      }
    };
    loadLookupOptions();
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    trigger,
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: yupResolver(admissionSchema),
    mode: "all",
    shouldUnregister: false,
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      dateOfBirth: undefined,
      gender: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      roomNumber: "",
      bedNumber: "",
      ssn: "",
      admissionType: "",
      primaryCarePhysician: "",
      attendingPhysicianPhone: "",
      attendingPhysicianEmail: "",
      pcpAddress: "",
      pcpNPI: "",
      preferredPharmacy: "",
      medicalRecordNumber: "",
      attendingPhysician: "",
      admissionDate: undefined,
      bloodPressure: "",
      height: "",
      weight: "",
      bloodGroup: "",
      allergies: "",
      primaryDiagnosis: "",
      mentalCondition: "",
      physicalDisabilities: "",
      dnrStatus: "",
      hospitalName: "",
      doctorName: "",
      hospitalAddress: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: "",
      emergencyContactEmail: "",
      emergencyContactAddress: "",
      insuranceCompanyName: "",
      insuranceType: "",
      payerId: "",
      insurancePhone: "",
      insuranceEmail: "",
      subscriberName: "",
      subscriberRelationship: "",
      subscriberDob: null,
      subscriberGender: "",
      subscriberPhone: "",
      subscriberAddress: "",
      subscriberEmployerName: "",
      subscriberEmployerAddress: "",
      policyMemberId: "",
      groupNumber: "",
      planName: "",
      planType: "",
      coverageType: "",
      effectiveDate: null,
      terminationDate: null,
      referralRequired: false,
      priorAuthRequired: false,
      authorizationNumber: "",
      copayAmount: "",
      coinsurancePercentage: "",
      annualDeductible: "",
      remainingDeductible: "",
      outOfPocketLimit: "",
      billingProviderNpi: "",
      billingProviderTaxId: "",
      billingProviderAddress: "",
      renderingProviderName: "",
      renderingProviderNpi: "",
      facilityNpi: "",
      placeOfServiceCode: "",
      icd10DiagnosisCodes: "",
      cptHcpcsCodes: "",
      modifiers: "",
      chargeAmount: "",
      claimNotes: "",
      claimSubmissionMethod: "",
      documentName: "",
    },
  });

  const stepSchemas = [
    // Step 0: Personal
    [
      "firstName",
      "middleName",
      "lastName",
      "dateOfBirth",
      "gender",
      "address",
      "city",
      "state",
      "zipCode",
      "roomNumber",
      "bedNumber",
      "ssn",
      "admissionType",
    ],
    // Step 1: Clinical
    [
      "admissionDate",
      "attendingPhysician",
      "subscriberPhone",
      "primaryDiagnosis",
      "bloodPressure",
      "height",
      "weight",
      "bloodGroup",
      "allergies",
      "medicalRecordNumber",
      "mentalCondition",
      "physicalDisabilities",
      "dnrStatus",
      "hospitalName",
      "doctorName",
      "hospitalAddress",
      "primaryCarePhysician",
      "attendingPhysicianPhone",
      "attendingPhysicianEmail",
      "pcpAddress",
      "pcpNPI",
      "preferredPharmacy",
    ],
    // Step 2: Insurance (includes Policy Details and Billing Fields)
    [
      "insuranceCompanyName",
      "insuranceType",
      "payerId",
      "insurancePhone",
      "insuranceEmail",
      "subscriberName",
      "subscriberRelationship",
      "subscriberDob",
      "subscriberGender",
      "subscriberPhone",
      "subscriberAddress",
      "subscriberEmployerName",
      "subscriberEmployerAddress",
      "policyMemberId",
      "groupNumber",
      "planName",
      "planType",
      "coverageType",
      "effectiveDate",
      "terminationDate",
      "referralRequired",
      "priorAuthRequired",
      "authorizationNumber",
      "copayAmount",
      "coinsurancePercentage",
      "annualDeductible",
      "remainingDeductible",
      "outOfPocketLimit",
      "billingProviderNpi",
      "billingProviderTaxId",
      "billingProviderAddress",
      "renderingProviderName",
      "renderingProviderNpi",
      "facilityNpi",
      "placeOfServiceCode",
      "icd10DiagnosisCodes",
      "cptHcpcsCodes",
      "modifiers",
      "chargeAmount",
      "claimNotes",
      "claimSubmissionMethod",
    ],
    // Step 3: Upload
    [],
    // Step 4: Review
    [],
  ];


  useEffect(() => {
    if (isEditMode && residentId) {
      if (!selectedResident) {
        getResidentById(residentId);
      }
      fetchResidentDocuments(residentId);
    }
  }, [
    isEditMode,
    residentId,
    selectedResident,
    getResidentById,
    fetchResidentDocuments,
  ]);

  useEffect(() => {
    if (isEditMode && residentDocuments) {
      // Ensure users are loaded if not already (autoFetch covers init, but good to be safe or rely on autoFetch)

      const existingDocs: UploadedDocument[] = residentDocuments.map((doc) => {
        // Prefer backend-provided display name if available
        let createdByName = doc.createdByName || doc.createdBy || "";
        const userId = doc.uploadedBy || doc.createdBy;

        // If backend didn't provide a display name, try resolving from users list or current user
        if ((!doc.createdByName || doc.createdByName === "") && userId) {
          if (users && users.length > 0) {
            const u = users.find(u =>
              (u.id && u.id.toLowerCase() === String(userId).toLowerCase()) ||
              (u.keycloakId && u.keycloakId.toLowerCase() === String(userId).toLowerCase()) ||
              u.username === userId
            );
            if (u) {
              createdByName = u.fullName || `${u.firstName} ${u.lastName}`;
            }
          }

          if ((!createdByName || createdByName === String(userId)) && user && (user.id === userId || user.username === userId)) {
            createdByName = user.name || user.username;
          }
        }

        return {
          id: doc.id,
          name: doc.fileName, // Swap: Backend seems to populate fileName with the user-entered document name
          fileName: doc.documentName, // Swap: Backend seems to populate documentName with content-type/filename
          file: null,
          isExisting: true,
          createdAt: doc.createdAt || doc.uploadedAt,
          createdBy: createdByName,
        } as UploadedDocument;
      });

      setUploadedDocuments(prev => {
        // Keep newly added local documents that haven't been saved to server yet
        const localNewDocs = prev.filter(d => !d.isExisting);
        return [...existingDocs, ...localNewDocs];
      });
    }
  }, [isEditMode, residentDocuments, users]);

  const onSubmit = async () => {
    try {
      const isValid = await trigger([
        "firstName",
        "lastName",
        "gender",
        "dateOfBirth",
        "address",
        "roomNumber",
        "bedNumber",
        "admissionType",
        "admissionDate",
        "medicalRecordNumber",
        "attendingPhysician",
        "attendingPhysicianPhone",
        "attendingPhysicianEmail",
        "primaryCarePhysician",
        "dnrStatus",
        "bloodPressure",
        "height",
        "weight",
        "emergencyContactPhone",
        "insuranceType",
        "insurancePhone",
        "subscriberRelationship",
        "subscriberGender",
        "policyMemberId",
        "planType",
        "coverageType",
        "claimSubmissionMethod",
        "billingProviderNpi",
        "billingProviderTaxId",
        "renderingProviderNpi",
        "facilityNpi",
        "placeOfServiceCode",
        "icd10DiagnosisCodes",
        "cptHcpcsCodes",
        "chargeAmount",
        "authorizationNumber",
        "copayAmount",
        "coinsurancePercentage",
        "annualDeductible",
        "remainingDeductible",
        "outOfPocketLimit",
      ]);

      if (!isValid) {
        setErrorMessage("Please fill in all required fields");
        setErrorOpen(true);
        return;
      }

      const rawData = getValues();
      if (rawData.billingProviderNpi && !isValidNpi(rawData.billingProviderNpi)) {
        setErrorMessage("Please enter a valid 10-digit NPI.");
        setErrorOpen(true);
        return;
      }
      if (rawData.billingProviderTaxId && !isValidTaxId(rawData.billingProviderTaxId)) {
        setErrorMessage("Please enter a valid 9-digit Tax ID (EIN).");
        setErrorOpen(true);
        return;
      }
      if (rawData.renderingProviderNpi && !isValidNpi(rawData.renderingProviderNpi)) {
        setErrorMessage("Please enter a valid 10-digit NPI.");
        setErrorOpen(true);
        return;
      }
      if (rawData.facilityNpi && !isValidNpi(rawData.facilityNpi)) {
        setErrorMessage("Please enter a valid 10-digit NPI.");
        setErrorOpen(true);
        return;
      }
      if (rawData.placeOfServiceCode && !isValidPosCode(rawData.placeOfServiceCode)) {
        setErrorMessage("Please enter a valid 2-digit POS code.");
        setErrorOpen(true);
        return;
      }
      if (rawData.cptHcpcsCodes && validateCptHcpcsCodes(rawData.cptHcpcsCodes) !== "") {
        setErrorMessage("Please enter a valid CPT/HCPCS code.");
        setErrorOpen(true);
        return;
      }
      if (rawData.chargeAmount && validateChargeAmount(rawData.chargeAmount) !== "") {
        setErrorMessage(validateChargeAmount(rawData.chargeAmount));
        setErrorOpen(true);
        return;
      }
      if (rawData.priorAuthRequired && (!rawData.authorizationNumber || !rawData.authorizationNumber.trim())) {
        setErrorMessage("Authorization Number is required when Prior Authorization is required.");
        setErrorOpen(true);
        return;
      }
      if (rawData.authorizationNumber && validateAuthorizationNumber(rawData.authorizationNumber) !== "") {
        setErrorMessage("Please enter a valid authorization number.");
        setErrorOpen(true);
        return;
      }
      if (rawData.copayAmount && validateCopayAmount(rawData.copayAmount) !== "") {
        setErrorMessage("Please enter a valid copay amount.");
        setErrorOpen(true);
        return;
      }
      if (rawData.coinsurancePercentage && validateCoinsurancePercentage(rawData.coinsurancePercentage) !== "") {
        setErrorMessage("Coinsurance percentage must be between 0 and 100.");
        setErrorOpen(true);
        return;
      }
      if (rawData.annualDeductible && validateAnnualDeductible(rawData.annualDeductible) !== "") {
        setErrorMessage("Please enter a valid annual deductible amount.");
        setErrorOpen(true);
        return;
      }
      if (rawData.remainingDeductible) {
        const remainingErr = validateRemainingDeductible(rawData.remainingDeductible, rawData.annualDeductible);
        if (remainingErr) {
          setErrorMessage(remainingErr);
          setErrorOpen(true);
          return;
        }
      }
      if (rawData.outOfPocketLimit && validateOutOfPocketLimit(rawData.outOfPocketLimit) !== "") {
        setErrorMessage("Please enter a valid out-of-pocket limit.");
        setErrorOpen(true);
        return;
      }
      const finalData = {
        ...rawData,
        // REQUIRED Backend Fields - Add these defaults
        firstName: rawData.firstName || "John",
        gender: rawData.gender || "Male",
        // careLevel: rawData.careLevel || "MEDIUM",
        attendingPhysicianName: rawData.attendingPhysician || null,
        // Emergency Contact — sent independently from insurance
        emergencyContactName: rawData.emergencyContactName || "",
        emergencyContactRelationship: rawData.emergencyContactRelationship || "",
        emergencyContactPhone: rawData.emergencyContactPhone || "",
        emergencyContactEmail: rawData.emergencyContactEmail || "",
        emergencyContactAddress: rawData.emergencyContactAddress || "",

        // Date formatting
        dateOfBirth: rawData.dateOfBirth
          ? format(rawData.dateOfBirth, "yyyy-MM-dd")
          : null,
        admissionDate: formatToLocalDateTime(rawData.admissionDate),

        // Map zipcode to postalCode for backend DTO payload
        postalCode: rawData.zipCode || rawData.postalCode || "",
        zipCode: rawData.zipCode || rawData.postalCode || "",

        // Field mapping (keep these)
        insuranceProvider: rawData.insuranceCompanyName,
        insurancePolicyNumber: rawData.policyMemberId,
        insuranceGroupNumber: rawData.groupNumber,

        // Ensure BP is sent as 'bp' as well, in case backend expects legacy field
        bp: rawData.bloodPressure,
      };

      const cleanData: any = {};
      Object.entries(finalData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          cleanData[key] = value;
        }
      });



      if (isEditMode && residentId) {
        await updateResident(residentId, cleanData);

        if (uploadedDocuments.length > 0) {
          // Filter only new files
          const newDocs = uploadedDocuments.filter(doc => doc.file);

          if (newDocs.length > 0) {
            try {
              // Ensure docType is set
              const docsToUpload = newDocs.map(d => ({
                ...d,
                documentType: 'Admission'
              }));

              await uploadResidentDocuments(residentId, docsToUpload);
            } catch (e) {
              console.error("Failed to upload doc on edit", e);
            }
          }
        }

        // SAVE VITALS ON EDIT REMOVED as per user request to not add to vitals history
        // The data is still sent to updateResident via cleanData (bp, weight, height)

        setSuccessMessage("Resident updated successfully!");
        setSuccessOpen(true);

        setTimeout(() => {
          navigate("/residents", {
            state: { success: "Resident updated successfully!", refresh: true },
          });
        }, 2000);
      } else {
        const newResident = await createResident(cleanData);


        if (uploadedDocuments.length > 0 && newResident.id) {
          try {
            const docsToUpload = uploadedDocuments
              .filter(d => d.file)
              .map(d => ({
                ...d,
                documentType: 'Admission',
                createdBy: user ? (user.name || user.username) : "Current User",
                createdAt: formatToBackendDateTime(new Date()) || "",
              }));

            if (docsToUpload.length > 0) {
              await uploadResidentDocuments(newResident.id, docsToUpload);
            }

            setSuccessMessage(
              `Resident "${newResident.firstName} ${newResident.lastName}" created with ${uploadedDocuments.length} document(s) uploaded successfully!`
            );
          } catch (uploadError) {
            console.error("Document upload failed:", uploadError);
            setSuccessMessage(
              `Resident "${newResident.firstName} ${newResident.lastName}" created, but some documents failed to upload`
            );
          }
        }

        // SAVE INITIAL VITALS (BP, Height, Weight)
        if (newResident.id && (cleanData.bloodPressure || cleanData.weight || cleanData.height)) {
          try {

            await vitalsService.createVitals({
              residentId: newResident.id,
              dailyBloodPressure: cleanData.bloodPressure || "",
              dailyBloodSugar: "",
              dailyWeight: cleanData.weight ? String(cleanData.weight) : "",
              temperature: "",
              pulseRate: "",
              respirationRate: "",
              spO2: "",
              painLevel: "",
              levelOfConsciousness: "",
              mealPortion: "",
              bowelMovement: "",
              vitalDate: formatToBackendDateTime(new Date()) || '',
            });

          } catch (vitalsError) {
            console.error("Failed to save initial vitals:", vitalsError);
            // Don't block success flow, just log it.
          }
        }

        if (uploadedDocuments.length === 0) {
          setSuccessMessage(
            `Resident "${newResident.firstName} ${newResident.lastName}" created successfully!`
          );
        }

        setSuccessOpen(true);
        setAdmissionSuccess(true);

        setTimeout(() => {
          navigate("/residents", {
            state: {
              success: `Resident "${newResident.firstName} ${newResident.lastName}" created!`,
              refresh: true,
            },
          });
        }, 2000);
      }
    } catch (error: any) {
      console.error("❌ Submit failed:", error);
      let errorMsg = "Failed to submit form. Please try again.";

      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.validationErrors) {
          const validationErrors = errorData.validationErrors;
          const errorMessages = Object.entries(validationErrors)
            .map(([field, messages]) => {
              const fieldName = field
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase());
              return `${fieldName}: ${Array.isArray(messages) ? messages.join(", ") : messages
                }`;
            })
            .join("\n");
          errorMsg = errorMessages;
        } else if (errorData.message) {
          errorMsg = errorData.message;
        } else if (errorData.error) {
          errorMsg = errorData.error;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      setErrorMessage(errorMsg);
      setErrorOpen(true);
    }
  };

  const onError = (formErrors: any) => {
    setErrorMessage("Please fill in all required fields marked in red.");
    setErrorOpen(true);

    const firstErrorKey = Object.keys(formErrors)[0];
    if (firstErrorKey) {
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`) as HTMLElement;
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });

          if (errorElement.getAttribute("type") !== "hidden") {
            errorElement.focus({ preventScroll: true });
          } else {
            const parent = errorElement.parentElement;
            if (parent) {
              const focusable = parent.querySelector('[tabindex]') as HTMLElement;
              if (focusable) focusable.focus({ preventScroll: true });
            }
          }
        }
      }, 100);
    }
  };

  useEffect(() => {
    if (selectedResident && isEditMode) {

      const formValues = mapResidentToFormData(selectedResident);
      Object.entries(formValues).forEach(([key, value]) => {
        setValue(key as keyof FormData, value);
      });
      setFormData(formValues as FormData);
      setActiveStep(0);
    }
  }, [selectedResident, isEditMode, setValue]);

  const mrnValue = watch("medicalRecordNumber");

  useEffect(() => {
    const checkMRNUniqueness = async () => {
      if (!mrnValue || mrnValue.trim() === "") {
        setMrnError(null);
        clearErrors("medicalRecordNumber");
        return;
      }

      // If in edit mode and MRN is same as original, no need to check
      if (isEditMode && selectedResident && mrnValue === (selectedResident.medicalRecordNumber || selectedResident.emrNumber)) {
        setMrnError(null);
        clearErrors("medicalRecordNumber");
        return;
      }

      setMrnChecking(true);
      try {
        // Use the new quickSearch API
        // Assuming user's facilityId is needed. We can get it from selectedResident or auth.
        // For simplicity, let's look for any matching MRN across facilities OR use context.
        const facilityId = selectedResident?.facilityId || (user as any)?.facility?.[0]?.id; // Fallback to user's first facility

        if (!facilityId) {
          console.warn("No facilityId found for MRN check");
          setMrnChecking(false);
          return;
        }

        const matches = await residentService.quickSearch(facilityId, mrnValue);
        const isDuplicate = matches.some(
          (r: ResidentDTO) => {
            const recordVal = r.medicalRecordNumber || r.emrNumber;
            return recordVal?.toLowerCase() === mrnValue.trim().toLowerCase() && String(r.id) !== String(residentId);
          }
        );

        if (isDuplicate) {
          const msg = "Medical Record Number (MRN) already exists. Please enter a unique number.";
          setMrnError(msg);
          setError("medicalRecordNumber", { type: "manual", message: msg });
        } else {
          setMrnError(null);
          clearErrors("medicalRecordNumber");
        }
      } catch (err) {
        console.error("Error checking MRN uniqueness:", err);
      } finally {
        setMrnChecking(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      checkMRNUniqueness();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [mrnValue, residentId, isEditMode, selectedResident, setError, clearErrors, user]);

  const onNext = async () => {
    const fields = stepSchemas[activeStep];
    if (fields.length > 0) {
      const valid = await trigger(fields as any);
      if (!valid) {
        setErrorMessage("Please fill in all required fields marked in red.");
        setErrorOpen(true);
        return;
      }
    }

    const currentData = getValues();
    setFormData({ ...formData, ...currentData });

    // Mark personal info as completed when moving past step 0
    if (activeStep === 0) {
      setPersonalInfoCompleted(true);
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setAdmissionSuccess(false);
    setFormData(null);
    setUploadedDocuments([]);
    setPersonalInfoCompleted(false);
  }, []);

  // Shared field style — eliminates ~60 duplicate sx props
  const fieldSx = useMemo(() => ({
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
    "& .MuiInputLabel-root": {
      color: "#1A2B45",
    },
    "& .MuiFormLabel-asterisk": {
      color: "#ef4444",
    },
    "& .MuiInputBase-input, & .MuiSelect-select": {
      color: "#1A2B45",
      WebkitTextFillColor: "#1A2B45",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#1A2B45",
      opacity: 1,
    },
  }), []);

  const handleFileSelect = useCallback((e: any) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentFile(file);
    }
  }, []);

  const handleAddDocument = useCallback(() => {
    if (currentFile && currentDocName) {
      const newDocument: UploadedDocument = {
        id: Date.now().toString(),
        name: currentDocName,
        fileName: currentFile.name,
        file: currentFile,
        createdAt: formatToBackendDateTime(new Date()) || "",
        createdBy: user?.name || user?.username || "Unknown",
      };
      setUploadedDocuments((prev) => [...prev, newDocument]);
      setCurrentDocName("");
      setCurrentFile(null);
    }
  }, [currentFile, currentDocName, user]);

  const mapResidentToFormData = (resident: any): Partial<FormData> => ({
    firstName: resident.firstName || "",
    middleName: resident.middleName || "",
    lastName: resident.lastName || "",
    dateOfBirth: resident.dateOfBirth
      ? new Date(resident.dateOfBirth)
      : undefined,
    gender: resident.gender || "",
    address: resident.address || "",
    city: resident.city || "",
    state: resident.state || "",
    zipCode: resident.postalCode || resident.zipCode || "",
    roomNumber: resident.roomNumber || "",
    bedNumber: resident.bedNumber || "",
    ssn: resident.ssn || "",
    admissionType: resident.admissionType || "",
    primaryCarePhysician: resident.primaryCarePhysician || "",
    attendingPhysicianPhone: resident.attendingPhysicianPhone || "",
    attendingPhysicianEmail: resident.attendingPhysicianEmail || "",
    pcpAddress: resident.pcpAddress || "",
    pcpNPI: resident.pcpNpi || "",
    preferredPharmacy: resident.preferredPharmacy || "",

    admissionDate: resident.admissionDate
      ? new Date(resident.admissionDate)
      : undefined,
    attendingPhysician: resident.attendingPhysicianName || "",
    primaryDiagnosis: resident.primaryDiagnosis || "",
    bloodPressure: resident.bloodPressure || "",
    height: resident.height || "",
    weight: resident.weight || "",
    bloodGroup: resident.bloodGroup || "",
    allergies: resident.allergies || "",
    medicalRecordNumber: resident.medicalRecordNumber || "",
    mentalCondition: resident.mentalCondition || "",
    physicalDisabilities: resident.physicalDisabilities || "",
    dnrStatus: resident.dnrStatus || "",
    hospitalName: resident.hospitalName || "",
    doctorName: resident.doctorName || "",
    hospitalAddress: resident.hospitalAddress || "",

    // Emergency Contact (independent)
    emergencyContactName: resident.emergencyContactName || "",
    emergencyContactRelationship: resident.emergencyContactRelationship || "",
    emergencyContactPhone: resident.emergencyContactPhone || "",
    emergencyContactEmail: resident.emergencyContactEmail || "",
    emergencyContactAddress: resident.emergencyContactAddress || "",

    insuranceCompanyName:
      resident.insuranceCompanyName || resident.insuranceProvider || "",
    insuranceType: resident.insuranceType || "",
    payerId: resident.payerId || "",
    insurancePhone: resident.insurancePhone || "",
    insuranceEmail: resident.insuranceEmail || "",
    subscriberName: resident.subscriberName || "",
    subscriberRelationship: resident.subscriberRelationship || "Self",
    subscriberDob: resident.subscriberDob
      ? new Date(resident.subscriberDob)
      : null,
    subscriberGender: resident.subscriberGender || "",
    subscriberPhone: resident.subscriberPhone || "",
    subscriberAddress: resident.subscriberAddress || "",
    subscriberEmployerName: resident.subscriberEmployerName || "",
    subscriberEmployerAddress: resident.subscriberEmployerAddress || "",

    policyMemberId:
      resident.policyMemberId || resident.insurancePolicyNumber || "",
    groupNumber: resident.groupNumber || resident.insuranceGroupNumber || "",
    planName: resident.planName || "",
    planType: resident.planType || "",
    coverageType: resident.coverageType || "",
    effectiveDate: resident.effectiveDate
      ? new Date(resident.effectiveDate)
      : null,
    terminationDate: resident.terminationDate
      ? new Date(resident.terminationDate)
      : null,
    referralRequired: resident.requiresReferral
      ? resident.requiresReferral
      : false,
    priorAuthRequired: resident.priorAuthRequired
      ? resident.priorAuthRequired
      : false,
    authorizationNumber: resident.authorizationNumber || "",
    copayAmount: resident.copayAmount || "",
    coinsurancePercentage: resident.coinsurancePercentage?.toString() || "",
    annualDeductible: resident.annualDeductible?.toString() || "",
    remainingDeductible: resident.remainingDeductible?.toString() || "",
    outOfPocketLimit: resident.outOfPocketLimit?.toString() || "",

    billingProviderNpi: resident.billingProviderNpi || "",
    billingProviderTaxId: resident.billingProviderTaxId || "",
    billingProviderAddress: resident.billingProviderAddress || "",
    renderingProviderName: resident.renderingProviderName || "",
    renderingProviderNpi: resident.renderingProviderNpi || "",
    facilityNpi: resident.facilityNpi || "",
    placeOfServiceCode: resident.placeOfServiceCode || "",
    icd10DiagnosisCodes: resident.icd10DiagnosisCodes || "",
    cptHcpcsCodes: resident.cptHcpcsCodes || "",
    modifiers: resident.modifiers || "",
    chargeAmount: resident.chargeAmount?.toString() || "",
    claimNotes: resident.claimNotes || "",
    claimSubmissionMethod: resident.claimSubmissionMethod || "",

    documentName: "",
  });

  useEffect(() => {
    if (isEditMode && residentId && !selectedResident) {
      getResidentById(residentId);
    }
  }, [isEditMode, residentId, getResidentById, selectedResident]);

  const handleDeleteDocument = (id: string) => {
    setDocToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;

    setIsDeleting(true);
    const doc = uploadedDocuments.find((d) => d.id === docToDelete);

    if (doc?.isExisting && residentId) {
      try {
        await deleteResidentDocument(residentId, docToDelete);
        setSuccessMessage("Document deleted successfully");
        setSuccessOpen(true);
      } catch (err) {
        setErrorMessage("Failed to delete document from server");
        setErrorOpen(true);
      }
    } else {
      setUploadedDocuments(uploadedDocuments.filter((d) => d.id !== docToDelete));
    }

    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setDocToDelete(null);
  };

  // Function
  const handleViewDocument = async (doc: UploadedDocument) => {
    if (doc.isExisting) {
      await viewResidentDocument(doc.id, doc.fileName);
    } else if (doc.file) {
      const url = URL.createObjectURL(doc.file);
      window.open(url, "_blank");
    }
  };

  const renderStepContent = () => {
    return (
      <Box
        sx={{
          pb: 2,
          "& .MuiInputBase-input, & .MuiSelect-select": {
            color: "#1A2B45 !important",
            WebkitTextFillColor: "#1A2B45",
          },
          "& .MuiInputLabel-root": {
            color: "#1A2B45 !important",
          },
          "& .MuiInputBase-input::placeholder": {
            color: "#1A2B45",
            opacity: 1,
          },
        }}
      >
        {/* ── Personal Information ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon /> Personal Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Controller name="firstName" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="First Name" required
                    error={!!errors.firstName} helperText={errors.firstName?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 100 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="middleName" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Middle Name"
                    error={!!errors.middleName} helperText={errors.middleName?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 100 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="lastName" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Last Name" required
                    error={!!errors.lastName} helperText={errors.lastName?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 100 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller name="dateOfBirth" control={control}
                  render={({ field }) => (
                    <DatePicker label="Date of Birth"
                      value={field.value || null}
                      minDate={new Date('1900-01-01')}
                      maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                      format="dd/MM/yyyy"
                      onChange={(value) => { field.onChange(value); trigger("dateOfBirth"); }}
                      slotProps={{ textField: { onBlur: field.onBlur, fullWidth: true, required: true, error: !!errors.dateOfBirth, helperText: errors.dateOfBirth?.message, sx: fieldSx } }} />
                  )} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Controller name="ssn" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="SSN"
                    type={showSSN ? "text" : "password"}
                    placeholder="XXX-XX-XXXX"
                    error={!!errors.ssn} helperText={errors.ssn?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ maxLength: 11 }}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      let formatted = val;
                      if (val.length > 3 && val.length <= 5) {
                        formatted = `${val.slice(0, 3)}-${val.slice(3)}`;
                      } else if (val.length > 5) {
                        formatted = `${val.slice(0, 3)}-${val.slice(3, 5)}-${val.slice(5, 9)}`;
                      }
                      field.onChange(formatted);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowSSN(!showSSN)} edge="end" size="small">
                            {showSSN ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller name="gender" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.gender} required sx={fieldSx}>
                    <InputLabel>Gender</InputLabel>
                    <Select {...field} value={field.value || ""} label="Gender" onChange={(e) => field.onChange(e.target.value)}>
                      {genderOptions.map((g) => (
                        <MenuItem key={g.valueName} value={g.valueName}>{g.valueName}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.gender?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Controller name="roomNumber" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Room No." required
                    error={!!errors.roomNumber} helperText={errors.roomNumber?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                )} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Controller name="bedNumber" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Bed No." required
                    error={!!errors.bedNumber} helperText={errors.bedNumber?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller name="admissionDate" control={control}
                  render={({ field }) => (
                    <DatePicker label="Admission Date" value={field.value} onChange={field.onChange}
                      maxDate={new Date()} format="dd/MM/yyyy"
                      slotProps={{ textField: { onBlur: field.onBlur, fullWidth: true, required: true, error: !!errors.admissionDate, helperText: errors.admissionDate?.message, sx: fieldSx } }} />
                  )} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Controller name="address" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Address" required
                    error={!!errors.address} helperText={errors.address?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z0-9\s,.-]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="city" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="City"
                    error={!!errors.city} helperText={errors.city?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 100 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="state" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="State"
                    error={!!errors.state} helperText={errors.state?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 100 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="zipCode" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Zip Code"
                    error={!!errors.zipCode} helperText={errors.zipCode?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 10 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))} />
                )} />
            </Grid>

          </Grid >
        </Box >

        {/* ── Clinician Details ── */}
        < Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalHospitalIcon /> Clinician Details
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            {mrnError && (
              <Grid item xs={12}>
                <Alert severity="error">{mrnError}</Alert>
              </Grid>
            )}

            {/* Row 1: MRN, Attending Physician, Hospital Name */}
            <Grid item xs={12} sm={4}>
              <Controller name="medicalRecordNumber" control={control}
                rules={{ required: "Medical Record Number is required", pattern: { value: /^[A-Za-z0-9]+$/, message: "Only alphabets and numbers are allowed" } }}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Medical Record Number"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 10))}
                    error={!!errors.medicalRecordNumber || !!mrnError}
                    helperText={errors.medicalRecordNumber?.message || (mrnChecking ? "Checking uniqueness..." : "")}
                    required
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="attendingPhysician" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Attending Physician"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.attendingPhysician} required helperText={errors.attendingPhysician?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="hospitalName" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Hospital Name"
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                )} />
            </Grid>

            {/* Row 2: Contact Number, Email, Address */}
            <Grid item xs={12} sm={4}>
              <Controller name="attendingPhysicianPhone" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Contact Number" required
                    error={!!errors.attendingPhysicianPhone} helperText={errors.attendingPhysicianPhone?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="attendingPhysicianEmail"
                control={control}
                rules={{
                  required: "Email Address is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    label="Email Address"
                    required
                    type="email"
                    error={!!error || !!errors.attendingPhysicianEmail}
                    helperText={error?.message || errors.attendingPhysicianEmail?.message}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                    onBlur={(e) => {
                      field.onBlur();
                      trigger("attendingPhysicianEmail");
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      trigger("attendingPhysicianEmail");
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="pcpAddress" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Address"
                    error={!!errors.pcpAddress} helperText={errors.pcpAddress?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                )} />
            </Grid>

            {/* Row 3: Primary Care Physician, Preferred Pharmacy, PCP NPI */}
            <Grid item xs={12} sm={4}>
              <Controller name="primaryCarePhysician" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Primary Care Physician" required
                    error={!!errors.primaryCarePhysician} helperText={errors.primaryCarePhysician?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="preferredPharmacy" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Preferred Pharmacy"
                    error={!!errors.preferredPharmacy} helperText={errors.preferredPharmacy?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="pcpNPI" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="PCP NPI"
                    error={!!errors.pcpNPI} helperText={errors.pcpNPI?.message}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))} />
                )} />
            </Grid>

            {/* Row 4: Level of Care, DNR Status */}
            <Grid item xs={12} sm={6}>
              <Controller name="admissionType" control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.admissionType} sx={fieldSx} required>
                    <InputLabel id="admission-type-label">Level of Care</InputLabel>
                    <Select {...field} labelId="admission-type-label" label="Level of Care" value={field.value || ""}>
                      {admissionTypeOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>
                          {opt.valueName.charAt(0).toUpperCase() + opt.valueName.slice(1).toLowerCase()}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.admissionType?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="dnrStatus" control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.dnrStatus} sx={fieldSx} required>
                    <InputLabel id="dnr-status-label">DNR Status</InputLabel>
                    <Select
                      {...field}
                      labelId="dnr-status-label"
                      label="DNR Status"
                      value={field.value || ""}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 200, overflowY: 'auto' },
                        },
                      }}
                    >
                      {dnrStatusOptions.map(opt => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>
                          {opt.valueName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.dnrStatus?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>

            {/* Health Details (Uncollapsed) */}
            <Grid item xs={6} sm={3}>
              <Controller name="bloodPressure" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Blood Pressure" placeholder="120/80"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...fieldSx,
                      "& .MuiInputBase-input::placeholder": {
                        color: "#9CA3AF !important",
                        WebkitTextFillColor: "#9CA3AF !important",
                        opacity: "1 !important"
                      }
                    }}
                    required
                    error={!!errors.bloodPressure || (!!field.value && !/^\d{2,3}\/\d{2,3}$/.test(field.value))}
                    helperText={errors.bloodPressure?.message || (field.value && !/^\d{2,3}\/\d{2,3}$/.test(field.value) ? "Format: 120/80" : "")}
                    onChange={(e) => { if (/^[0-9/]*$/.test(e.target.value)) field.onChange(e.target.value); }} />
                )} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Controller name="height" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Height (inches)" value={field.value || ""}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    required
                    error={!!errors.height} helperText={errors.height?.message}
                    onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) field.onChange(e.target.value); }} />
                )} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Controller name="weight" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Weight (lbs)" value={field.value || ""}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    required
                    error={!!errors.weight} helperText={errors.weight?.message}
                    onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) field.onChange(e.target.value); }} />
                )} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Controller name="bloodGroup" control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.bloodGroup}>
                    <InputLabel id="blood-group-label">Blood Group</InputLabel>
                    <Select {...field} labelId="blood-group-label" label="Blood Group" value={field.value || ""}>
                      {bloodGroupOptions.map(opt => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>
                          {opt.valueName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="allergies" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Allergies (comma-separated)" multiline rows={2}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.allergies} helperText={errors.allergies?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="primaryDiagnosis" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Primary Diagnosis" multiline rows={2}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} inputProps={{ maxLength: 700 }}
                    error={!!errors.primaryDiagnosis} helperText={errors.primaryDiagnosis?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="mentalCondition" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Mental Condition" multiline rows={2}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.mentalCondition} helperText={errors.mentalCondition?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="physicalDisabilities" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Physical Disabilities" multiline rows={2}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.physicalDisabilities} helperText={errors.physicalDisabilities?.message} />
                )} />
            </Grid>

          </Grid>
        </Box >

        {/* ── Emergency Contact ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ContactEmergencyIcon /> Emergency Contact
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Controller name="emergencyContactName" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Emergency Contact Name" required
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.emergencyContactName} helperText={errors.emergencyContactName?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="emergencyContactRelationship" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.emergencyContactRelationship} sx={fieldSx}>
                    <InputLabel>Relation to Resident</InputLabel>
                    <Select {...field} value={field.value || ""} label="Relation to Resident"
                      onChange={(e) => {
                        const value = e.target.value;
                        const prevValue = field.value;
                        field.onChange(value);
                        if (value === "Self") {
                          const fn = getValues("firstName") || ""; const mn = getValues("middleName") || ""; const ln = getValues("lastName") || "";
                          setValue("emergencyContactName", `${fn} ${mn} ${ln}`.replace(/\s+/g, ' ').trim(), { shouldValidate: true });
                          setValue("emergencyContactAddress", getValues("address") || "", { shouldValidate: true });
                        } else if (prevValue === "Self") {
                          setValue("emergencyContactName", "", { shouldValidate: true });
                          setValue("emergencyContactAddress", "", { shouldValidate: true });
                        }
                      }}>
                      {relationshipOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.emergencyContactRelationship?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="emergencyContactPhone" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Emergency Contact Phone" required
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.emergencyContactPhone} helperText={errors.emergencyContactPhone?.message}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="emergencyContactEmail"
                control={control}
                rules={{
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    label="Email Address"
                    type="email"
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                    error={!!error || !!errors.emergencyContactEmail}
                    helperText={error?.message || errors.emergencyContactEmail?.message}
                    onBlur={(e) => {
                      field.onBlur();
                      trigger("emergencyContactEmail");
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      trigger("emergencyContactEmail");
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="emergencyContactAddress" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Address"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.emergencyContactAddress} helperText={errors.emergencyContactAddress?.message} />
                )} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Insurance Information ── */}
        < Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VerifiedUserIcon /> Insurance Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2.5}>
            {/* ── Essential Insurance Fields ── */}
            <Grid item xs={12} sm={6}>
              <Controller name="insuranceType" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.insuranceType}>
                    <InputLabel>Insurance Type</InputLabel>
                    <Select {...field} value={field.value || ""} label="Insurance Type" onChange={(e) => field.onChange(e.target.value)}>
                      {insuranceTypeOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.insuranceType?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="insuranceCompanyName" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Insurance Company Name"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.insuranceCompanyName} helperText={errors.insuranceCompanyName?.message}
                    onChange={(e) => { if (/^[A-Za-z\s]*$/.test(e.target.value)) field.onChange(e.target.value); }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="payerId" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Payer ID"
                    inputProps={{ maxLength: 35 }}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.payerId} helperText={errors.payerId?.message}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 35);
                      field.onChange(sanitized);
                    }}
                    onBlur={() => {
                      field.onChange((field.value || "").trim());
                      field.onBlur();
                    }}
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="policyMemberId" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Policy / Member ID"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.policyMemberId} helperText={errors.policyMemberId?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="subscriberRelationship" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.subscriberRelationship}>
                    <InputLabel>Relationship to Patient</InputLabel>
                    <Select {...field} value={field.value || ""} label="Relationship to Patient"
                      onChange={(e) => {
                        const value = e.target.value;
                        const prevValue = field.value;
                        field.onChange(value);
                        if (value === "Self") {
                          const fn = getValues("firstName") || ""; const mn = getValues("middleName") || ""; const ln = getValues("lastName") || "";
                          setValue("subscriberName", `${fn} ${mn} ${ln}`.replace(/\s+/g, ' ').trim(), { shouldValidate: true });
                          setValue("subscriberDob", getValues("dateOfBirth"), { shouldValidate: true });
                          setValue("subscriberGender", getValues("gender"), { shouldValidate: true });
                          setValue("subscriberAddress", getValues("address") || "", { shouldValidate: true });
                        } else if (prevValue === "Self") {
                          setValue("subscriberName", "", { shouldValidate: true });
                          setValue("subscriberDob", null, { shouldValidate: true });
                          setValue("subscriberGender", "", { shouldValidate: true });
                          setValue("subscriberAddress", "", { shouldValidate: true });
                        }
                      }}>
                      {relationshipOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.subscriberRelationship?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>

            {/* ── Subscriber / Policyholder Details (Uncollapsed) ── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Subscriber Details</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="subscriberName" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Subscriber Name"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.subscriberName} helperText={errors.subscriberName?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z ]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller name="subscriberDob" control={control}
                  render={({ field }) => (
                    <DatePicker label="Subscriber DOB" value={field.value} onChange={field.onChange}
                      disableFuture format="dd/MM/yyyy"
                      slotProps={{ textField: { fullWidth: true, error: !!errors.subscriberDob, helperText: errors.subscriberDob?.message } }} />
                  )} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="subscriberGender" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.subscriberGender}>
                    <InputLabel>Subscriber Gender</InputLabel>
                    <Select {...field} value={field.value || ""} label="Subscriber Gender" onChange={(e) => field.onChange(e.target.value)}>
                      {genderOptions.map((g) => (
                        <MenuItem key={g.valueName} value={g.valueName}>{g.valueName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="subscriberAddress" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Subscriber Address"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.subscriberAddress} helperText={errors.subscriberAddress?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="subscriberEmployerName" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Subscriber Employer Name"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.subscriberEmployerName} helperText={errors.subscriberEmployerName?.message}
                    onChange={(e) => { if (/^[A-Za-z\s]*$/.test(e.target.value)) field.onChange(e.target.value); }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="subscriberEmployerAddress" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Subscriber Employer Address"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.subscriberEmployerAddress} helperText={errors.subscriberEmployerAddress?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="insurancePhone" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Insurance Phone"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.insurancePhone} helperText={errors.insurancePhone?.message}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="insuranceEmail"
                control={control}
                rules={{
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    label="Insurance Email"
                    type="email"
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                    error={!!error || !!errors.insuranceEmail}
                    helperText={error?.message || errors.insuranceEmail?.message}
                    onBlur={(e) => {
                      field.onBlur();
                      trigger("insuranceEmail");
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      trigger("insuranceEmail");
                    }}
                  />
                )}
              />
            </Grid>
            {/* ── Policy Details (Uncollapsed) ── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Policy Details</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="planName" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Plan Name"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.planName} helperText={errors.planName?.message}
                    onChange={(e) => { if (/^[A-Za-z\s]*$/.test(e.target.value)) field.onChange(e.target.value); }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="planType" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.planType}>
                    <InputLabel>Plan Type</InputLabel>
                    <Select {...field} value={field.value || ""} label="Plan Type" onChange={(e) => field.onChange(e.target.value)}>
                      {planTypeOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="coverageType" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.coverageType}>
                    <InputLabel>Coverage Type</InputLabel>
                    <Select {...field} value={field.value || ""} label="Coverage Type" onChange={(e) => field.onChange(e.target.value)}>
                      {coverageTypeOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller name="effectiveDate" control={control}
                  render={({ field }) => (
                    <DatePicker label="Effective Date" value={field.value} format="dd/MM/yyyy"
                      onChange={(date) => { field.onChange(date); const td = getValues("terminationDate"); if (td && date && date > td) setValue("terminationDate", null); }}
                      slotProps={{ textField: { fullWidth: true, error: !!errors.effectiveDate, helperText: errors.effectiveDate?.message } }} />
                  )} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller name="terminationDate" control={control}
                  render={({ field }) => (
                    <DatePicker label="Termination Date" value={field.value} onChange={field.onChange}
                      format="dd/MM/yyyy" minDate={getValues("effectiveDate") || undefined}
                      slotProps={{ textField: { fullWidth: true, error: !!errors.terminationDate, helperText: errors.terminationDate?.message } }} />
                  )} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="referralRequired" control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Requires Referral?</FormLabel>
                    <RadioGroup row value={field.value === true ? "Yes" : field.value === false ? "No" : ""} onChange={(e) => field.onChange(e.target.value === "Yes")}>
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="priorAuthRequired" control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Prior Authorization Required?</FormLabel>
                    <RadioGroup row value={field.value === true ? "Yes" : field.value === false ? "No" : ""}
                      onChange={(e) => {
                        const isYes = e.target.value === "Yes";
                        field.onChange(isYes);
                        trigger("authorizationNumber");
                      }}>
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="authorizationNumber" control={control}
                render={({ field }) => {
                  const isRequired = watch("priorAuthRequired") === true;
                  return (
                    <TextField {...field} value={field.value || ""} fullWidth
                      label="Authorization Number"
                      required={isRequired}
                      InputLabelProps={{ shrink: true }} sx={fieldSx}
                      inputProps={{ maxLength: 30 }}
                      error={!!errors.authorizationNumber} helperText={errors.authorizationNumber?.message}
                      onKeyDown={(e) => {
                        if (e.key === " ") {
                          e.preventDefault();
                          return;
                        }
                        if (e.ctrlKey || e.metaKey || e.altKey) return;
                        if (
                          e.key === "Backspace" ||
                          e.key === "Delete" ||
                          e.key === "Tab" ||
                          e.key === "Enter" ||
                          e.key === "Escape" ||
                          e.key.startsWith("Arrow") ||
                          e.key === "Home" ||
                          e.key === "End"
                        ) {
                          return;
                        }
                        if (!/^[A-Za-z0-9]$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 30);
                        field.onChange(sanitized);
                      }}
                      onBlur={() => {
                        field.onChange((field.value || "").trim());
                        field.onBlur();
                      }}
                    />
                  );
                }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="copayAmount" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Copay Amount"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "decimal", maxLength: 10 }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", " "].includes(e.key)) e.preventDefault();
                    }}
                    error={!!errors.copayAmount} helperText={errors.copayAmount?.message}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      if (v === "" || /^\d{0,7}(\.\d{0,2})?$/.test(v)) field.onChange(v);
                    }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="coinsurancePercentage" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Coinsurance %"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "decimal", maxLength: 6 }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", " "].includes(e.key)) e.preventDefault();
                    }}
                    error={!!errors.coinsurancePercentage} helperText={errors.coinsurancePercentage?.message}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      if (v === "" || /^\d{0,3}(\.\d{0,2})?$/.test(v)) {
                        const num = parseFloat(v);
                        if (!isNaN(num) && num > 100) return;
                        field.onChange(v);
                      }
                    }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="annualDeductible" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Deductible (Annual)"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "decimal", maxLength: 11 }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", " "].includes(e.key)) e.preventDefault();
                    }}
                    error={!!errors.annualDeductible} helperText={errors.annualDeductible?.message}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      if (v === "" || /^\d{0,8}(\.\d{0,2})?$/.test(v)) {
                        field.onChange(v);
                        if (getValues("remainingDeductible")) {
                          trigger("remainingDeductible");
                        }
                      }
                    }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="remainingDeductible" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Remaining Deductible"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "decimal", maxLength: 11 }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", " "].includes(e.key)) e.preventDefault();
                    }}
                    error={!!errors.remainingDeductible} helperText={errors.remainingDeductible?.message}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      if (v === "" || /^\d{0,8}(\.\d{0,2})?$/.test(v)) field.onChange(v);
                    }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="outOfPocketLimit" control={control}
                render={({ field }) => (
                  <TextField {...field} value={field.value || ""} fullWidth label="Out-of-Pocket Limit"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "decimal", maxLength: 11 }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", " "].includes(e.key)) e.preventDefault();
                    }}
                    error={!!errors.outOfPocketLimit} helperText={errors.outOfPocketLimit?.message}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      if (v === "" || /^\d{0,8}(\.\d{0,2})?$/.test(v)) field.onChange(v);
                    }} />
                )} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Billing Information (Uncollapsed) ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentsIcon /> Billing Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Controller name="billingProviderNpi" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Billing Provider NPI"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
                    error={!!errors.billingProviderNpi} helperText={errors.billingProviderNpi?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="billingProviderTaxId" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Billing Provider Tax ID"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                    error={!!errors.billingProviderTaxId} helperText={errors.billingProviderTaxId?.message}
                    onChange={(e) => field.onChange(formatTaxId(e.target.value))} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="billingProviderAddress" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Billing Provider Address"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.billingProviderAddress} helperText={errors.billingProviderAddress?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="renderingProviderName" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Rendering Provider Name"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.renderingProviderName} helperText={errors.renderingProviderName?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="renderingProviderNpi" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Rendering Provider NPI"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
                    error={!!errors.renderingProviderNpi} helperText={errors.renderingProviderNpi?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="facilityNpi" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Facility NPI"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
                    error={!!errors.facilityNpi} helperText={errors.facilityNpi?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="placeOfServiceCode" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Place of Service (POS Code)"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 2 }}
                    error={!!errors.placeOfServiceCode} helperText={errors.placeOfServiceCode?.message}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="claimSubmissionMethod" control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormControl fullWidth error={!!errors.claimSubmissionMethod}>
                    <InputLabel>Claim Submission Method</InputLabel>
                    <Select {...field} value={field.value || ""} label="Claim Submission Method" onChange={(e) => field.onChange(e.target.value)}>
                      {claimSubmissionOptions.map((opt) => (
                        <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.claimSubmissionMethod?.message}</FormHelperText>
                  </FormControl>
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="icd10DiagnosisCodes" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="ICD-10 Diagnosis Codes"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ maxLength: 100 }}
                    error={!!errors.icd10DiagnosisCodes} helperText={errors.icd10DiagnosisCodes?.message}
                    onChange={(e) => field.onChange(e.target.value)} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="cptHcpcsCodes" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="CPT/HCPCS Codes"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ maxLength: 100 }}
                    error={!!errors.cptHcpcsCodes} helperText={errors.cptHcpcsCodes?.message}
                    onChange={(e) => field.onChange(formatCptHcpcsInput(e.target.value))} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="modifiers" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Modifiers"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.modifiers} helperText={errors.modifiers?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="chargeAmount" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Charge Amount"
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    inputProps={{ inputMode: "decimal", maxLength: 12 }}
                    error={!!errors.chargeAmount} helperText={errors.chargeAmount?.message}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      if (v.split(".").length <= 2) field.onChange(v);
                    }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="claimNotes" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Claim Notes" multiline rows={2}
                    InputLabelProps={{ shrink: true }} sx={fieldSx}
                    error={!!errors.claimNotes} helperText={errors.claimNotes?.message} />
                )} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Upload Documents ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FileUploadIcon /> Upload Documents
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Document Name"
                value={currentDocName}
                onChange={(e) => setCurrentDocName(e.target.value)}
                inputProps={{ inputMode: "text", maxLength: 700 }}
              />
            </Grid>


            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ height: "56px" }}
              >
                {currentFile ? currentFile.name : "Select File"}
                <input
                  type="file"
                  id="document-upload"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setCurrentFile(e.target.files[0]);
                    }
                  }}
                />
              </Button>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  height: "56px",
                  bgcolor: "#48CAE4",
                  color: "#000000",
                  fontWeight: "normal",
                  "&:hover": { bgcolor: "#3ab3cc" },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.26)"
                  }
                }}
                onClick={handleAddDocument}
                disabled={!currentDocName || !currentFile}
              >
                Add
              </Button>
            </Grid>
          </Grid>
          {/* ── Document Upload Section ── */}
          <Box sx={{ mt: 4 }}>
            {uploadedDocuments.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Uploaded Documents
                </Typography>
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Document Name</TableCell>
                          <TableCell>Created Date</TableCell>
                          <TableCell>Created By</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadedDocuments.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell sx={{ wordBreak: "break-word" }}>
                              {doc.name}
                            </TableCell>
                            <TableCell>
                              {doc.createdAt
                                ? format(
                                  new Date(
                                    doc.createdAt.endsWith("Z")
                                      ? doc.createdAt
                                      : doc.createdAt + "Z"
                                  ),
                                  "dd/MM/yyyy"
                                )
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {doc.createdBy || "-"}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewDocument(doc)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }; // End of renderStepContent

  return (
    <Box>
      {isEditMode && loading && !selectedResident && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading resident data...</Typography>
        </Box>
      )}

      {admissionSuccess ? (
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Admissions
          </Typography>

          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Resident admission successfully created!
            </Alert>
            <Typography variant="h6" gutterBottom>
              Resident has been admitted to the facility
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              The resident is now in the system and can be managed through the
              Residents section.
            </Typography>
            <Button variant="contained" onClick={handleReset}>
              Add Another Resident
            </Button>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ pb: 10, pt: 3 }}>
          {/* ── Header ── */}
          <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
            {isEditMode ? "Edit Resident" : "New Admission"}
          </Typography>

          {/* ── Form Content (Single Page Layout) ── */}
          <Paper sx={{ p: 4, borderRadius: "12px", boxShadow: 1, mb: 4 }}>
            <form id="admission-form">
              {renderStepContent()}
            </form>
          </Paper>

          {/* ── Primary Actions ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mt: 2,
              mb: 4
            }}
          >
            <Button variant="outlined" color="secondary" onClick={() => navigate("/residents")}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit, onError)}
              sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
            >
              {isEditMode ? "Update Resident" : "Submit Admission"}
            </Button>
          </Box>

          {/* Error Toast */}
          <Snackbar
            open={errorOpen}
            autoHideDuration={6000}
            onClose={() => setErrorOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setErrorOpen(false)}
              severity="error"
              sx={{ width: "100%", whiteSpace: "pre-line" }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>

          {/* Success Toast */}
          <Snackbar
            open={successOpen}
            autoHideDuration={4000}
            onClose={() => setSuccessOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSuccessOpen(false)}
              severity="success"
              sx={{ width: "100%" }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Confirm Delete Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: "20px" } }}
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1, pt: 3, px: 3 }}>
              <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
              <Typography fontWeight={800} fontSize="1rem">Confirm Delete</Typography>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
              <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                Are you sure you want to delete this document?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#475569" }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteDocument}
                variant="contained"
                disabled={isDeleting}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}
                startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default AdmissionsPage;
